import { Suspense } from 'react'
import { Navigate, Route, Routes } from 'react-router'
import { RequireAuth } from '@/auth/RequireAuth'
import { RequireRole } from '@/auth/RequireRole'
import { FounderLayout } from '@/features/founder/FounderLayout'
import { FundraisingLayout } from '@/features/fundraising/FundraisingLayout'
import { RequireFundraisingEnabled } from '@/features/fundraising/RequireFundraisingEnabled'
import { ContactDetailPage } from '@/features/crm/ContactDetailPage'
import { ContactsPage } from '@/features/crm/ContactsPage'
import { removedCrmRoutes } from '@/features/crm/legacyRoutes'
import { EventRedirect } from '@/features/crm/lists/EventRedirect'
import { ListPage } from '@/features/crm/lists/ListPage'
import { ListsPage } from '@/features/crm/lists/ListsPage'
import { OrganisationPage } from '@/features/crm/organisations/OrganisationPage'
import { OrganisationsPage } from '@/features/crm/organisations/OrganisationsPage'
import { AgreementsPage } from '@/features/crm/agreements/AgreementsPage'
import { OverzichtPage } from '@/features/crm/overzicht/OverzichtPage'
import { CrmImportPage } from '@/features/crm/import/CrmImportPage'
import { ImportResultPage as CrmImportResultPage } from '@/features/crm/import/ImportResultPage'
import { InsprekenPage } from '@/features/crm/inspreken/InsprekenPage'
import { RelatiesIndexRedirect } from '@/features/crm/RelatiesIndexRedirect'
import { RelatiesLayout } from '@/features/crm/RelatiesLayout'
import { SettingsPage as CrmSettingsPage } from '@/features/crm/settings/SettingsPage'
import { StaffMfaGate } from '@/features/mfa/StaffMfaGate'
import { CommandSearchHost } from '@/features/search/CommandSearchHost'
import { PostinvestLayout } from '@/features/postinvest/PostinvestLayout'
import { WasstraatLegacyRedirect } from '@/features/postinvest/wasstraat/WasstraatLegacyRedirect'
import { PreinvestLayout } from '@/features/preinvest/PreinvestLayout'
import { SparkyHost } from '@/features/sparky/SparkyHost'
import { LastModuleLayout } from '@/layouts/LastModuleLayout'
import { RootLayout } from '@/layouts/RootLayout'
import { TasksRedirect } from '@/layouts/TasksRedirect'
import { LandingPage } from '@/pages/LandingPage'
import { LoginPage } from '@/pages/LoginPage'
import { lazyPage } from '@/lib/lazyPage'
import { NotFoundPage } from '@/pages/NotFoundPage'
import { PageNote } from '@/components/PageNote'

// Every non-CRM page loads on demand (spec-67); the CRM imports above stay eager until the
// CRM stream converts them.
const FounderFormDetailPage = lazyPage(() => import('@/features/founder/FounderFormDetailPage'), 'FounderFormDetailPage')
const FounderHomePage = lazyPage(() => import('@/features/founder/FounderHomePage'), 'FounderHomePage')
const PublicFormPage = lazyPage(() => import('@/features/public-forms/PublicFormPage'), 'PublicFormPage')
const FundraisingBoardPage = lazyPage(() => import('@/features/fundraising/board/FundraisingBoardPage'), 'FundraisingBoardPage')
const FundraisingDashboardPage = lazyPage(() => import('@/features/fundraising/FundraisingDashboardPage'), 'FundraisingDashboardPage')
const InvestorDetailPage = lazyPage(() => import('@/features/fundraising/InvestorDetailPage'), 'InvestorDetailPage')
const InvestorsPage = lazyPage(() => import('@/features/fundraising/InvestorsPage'), 'InvestorsPage')
const MailsPage = lazyPage(() => import('@/features/fundraising/MailsPage'), 'MailsPage')
const SuggestiesPage = lazyPage(() => import('@/features/fundraising/SuggestiesPage'), 'SuggestiesPage')
const FundLeadsConnectionsPage = lazyPage(() => import('@/features/fundleads/ConnectionsPage'), 'ConnectionsPage')
const FundLeadsLeadDetailPage = lazyPage(() => import('@/features/fundleads/LeadDetailPage'), 'LeadDetailPage')
const FundLeadsLeadsPage = lazyPage(() => import('@/features/fundleads/LeadsPage'), 'LeadsPage')
const AiNotulenPage = lazyPage(() => import('@/features/preinvest/ai-notulen/AiNotulenPage'), 'AiNotulenPage')
const AnalyticsPage = lazyPage(() => import('@/features/preinvest/analytics/AnalyticsPage'), 'AnalyticsPage')
const BoardPage = lazyPage(() => import('@/features/preinvest/board/BoardPage'), 'BoardPage')
const DossierPage = lazyPage(() => import('@/features/preinvest/dossier/DossierPage'), 'DossierPage')
const DossiersPage = lazyPage(() => import('@/features/preinvest/dossiers/DossiersPage'), 'DossiersPage')
const IcPage = lazyPage(() => import('@/features/preinvest/ic/IcPage'), 'IcPage')
const ImportResultPage = lazyPage(() => import('@/features/preinvest/lead-import/ImportResultPage'), 'ImportResultPage')
const LeadImportPage = lazyPage(() => import('@/features/preinvest/lead-import/LeadImportPage'), 'LeadImportPage')
const LeadTextImportPage = lazyPage(
  () => import('@/features/preinvest/lead-import/LeadTextImportPage'),
  'LeadTextImportPage',
)
const AiUsagePage = lazyPage(() => import('@/features/preinvest/ai-usage/AiUsagePage'), 'AiUsagePage')
const RejectionReasonsPage = lazyPage(() => import('@/features/preinvest/rejection-reasons/RejectionReasonsPage'), 'RejectionReasonsPage')
const ReferralsPage = lazyPage(() => import('@/features/postinvest/referrals/ReferralsPage'), 'ReferralsPage')
const PostinvestDocumentsPage = lazyPage(() => import('@/features/postinvest/documents/DocumentsPage'), 'DocumentsPage')
const InvestmentThesisPage = lazyPage(() => import('@/features/preinvest/thesis/InvestmentThesisPage'), 'InvestmentThesisPage')
const PortfolioListPage = lazyPage(() => import('@/features/postinvest/portfolio/PortfolioListPage'), 'PortfolioListPage')
const MyTasksPage = lazyPage(() => import('@/features/tasks/MyTasksPage'), 'MyTasksPage')
const NdaDetailPage = lazyPage(() => import('@/pages/NdaDetailPage'), 'NdaDetailPage')
const NdaListPage = lazyPage(() => import('@/pages/NdaListPage'), 'NdaListPage')
const SecurityPage = lazyPage(() => import('@/pages/SecurityPage'), 'SecurityPage')
const SetPasswordPage = lazyPage(() => import('@/pages/SetPasswordPage'), 'SetPasswordPage')
const PostinvestDossierPage = lazyPage(() => import('@/features/postinvest/dossier/DossierPage'), 'PostinvestDossierPage')
const PortalRedirectPage = lazyPage(() => import('@/features/postinvest/portalRedirect/PortalRedirectPage'), 'PortalRedirectPage')

function App() {
  return (
    <Suspense fallback={<PageNote>Bezig met laden…</PageNote>}>
    <Routes>
      {/* RootLayout renders the app-wide VONK background shell on every route. */}
      <Route element={<RootLayout />}>
        <Route path="/login" element={<LoginPage />} />
        {/* Public: invited / password-recovery users land here (via AuthProvider)
            to choose a password. No role guard — the one-time invite/recovery
            session is established before this renders. */}
        <Route path="/wachtwoord-instellen" element={<SetPasswordPage />} />
        {/* Public: magic link form access (spec-28). No login required — access
            is controlled by the token and IP-binding. */}
        <Route path="/forms/:token" element={<PublicFormPage />} />
        <Route element={<RequireAuth />}>
          {/* Verplichte 2FA voor admin/team/fundraiser: zonder aal2 komt
              niemand de shell in. Founders passeren `/` op weg naar /founder
              en worden door de gate doorgelaten (FounderMfaGate doet daar het
              werk). */}
          <Route element={<StaffMfaGate />}>
            {/* spec-62: ⌘K search on every staff page, the landing page and /taken
                included. Founder routes sit outside it; the host itself shows
                nothing for roles the search endpoint does not answer. */}
            <Route element={<CommandSearchHost />}>
            <Route path="/" element={<LandingPage />} />
            {/* Staff shell: admin/team plus the fundraiser role (spec-54) —
                founders bounce to /founder. The fundraiser reaches pre-/post-
                invest, /relaties (only the fund-leads boards) and /beveiliging;
                admin/team-only areas are nested behind a second RequireRole. */}
            <Route element={<RequireRole allow={['admin', 'team', 'fundraiser']} />}>
              {/* SparkyHost adds the global copilot FAB + slide-over (spec-29) to
                  every staff page; nesting it inside RequireRole keeps it
                  invisible to founders. It hides the FAB itself for the
                  fundraiser role (sparky backend is admin/team only). */}
              <Route element={<SparkyHost />}>
                {/* MFA enrolment / security settings (spec-14b). Also for the
                    fundraiser: the fund-leads API requires aal2. */}
                {/* spec-64 U-011: inside the shell of the module you came from. */}
                <Route path="/beveiliging" element={<LastModuleLayout />}>
                  <Route index element={<SecurityPage />} />
                </Route>
                <Route element={<RequireRole allow={['admin', 'team']} />}>
                  {/* "Mijn taken" lives in the shell of every module (spec-64 U-011):
                      /{module}/taken. The old global route forwards to the last
                      module used, so the bell, ⌘K and bookmarks keep working. */}
                  <Route path="/taken" element={<TasksRedirect />} />
                </Route>
                <Route path="/preinvest" element={<PreinvestLayout />}>
                  <Route index element={<Navigate to="/preinvest/board" replace />} />
                  <Route path="board" element={<BoardPage />} />
                  <Route path="funnel" element={<Navigate to="/preinvest/analytics" replace />} />
                  <Route path="dossiers" element={<DossiersPage />} />
                  {/* Leads importeren (spec-60 B2): the wizard and the result of one import. */}
                  <Route path="leads/import" element={<LeadImportPage />} />
                  <Route path="leads/import/:id" element={<ImportResultPage />} />
                  {/* Leads uit tekst (spec-81): paste a mail or page, Claude makes the leads. */}
                  <Route path="leads/tekst" element={<LeadTextImportPage />} />
                  <Route path="deals/:id" element={<DossierPage />} />
                  <Route path="tasks" element={<Navigate to="/preinvest/taken" replace />} />
                  <Route element={<RequireRole allow={['admin', 'team']} />}>
                    <Route path="taken" element={<MyTasksPage module="preinvest" />} />
                  </Route>
                  <Route path="analytics" element={<AnalyticsPage />} />
                  <Route path="ic" element={<IcPage />} />
                  {/* Investeringsthese (spec-41 WP-B): the yardstick section 7 of
                      the IC pre-read is written against. Editing is admin-only —
                      the page itself renders read-only for team, matching the
                      backend (admin/team read, admin write). */}
                  <Route path="investeringsthese" element={<InvestmentThesisPage />} />
                  {/* Afwijsredenen (spec-60): admin-only list management. The page
                      itself shows a notice to non-admins; the API enforces 403. */}
                  <Route path="afwijsredenen" element={<RejectionReasonsPage />} />
                  {/* AI-verbruik (spec-78): admin-only; the page shows a notice to others. */}
                  <Route path="ai-verbruik" element={<AiUsagePage />} />
                  <Route path="ai-notulen" element={<AiNotulenPage />} />
                  {/* NDA pages are production-critical and unchanged; paths preserved. */}
                  <Route path="nda" element={<NdaListPage />} />
                  <Route path="nda/:id" element={<NdaDetailPage />} />
                  <Route path="*" element={<NotFoundPage />} />
                </Route>
                {/* Fundraising (spec-21): standalone investor pipeline dashboard.
                    Archived behind FUNDRAISING_UI_ENABLED (spec-30): the guard
                    redirects every /fundraising path to `/` while the flag is
                    off, so the module stays in the codebase but is unreachable.
                    Admin/team only — despite the name this is NOT the
                    fundraiser's fund-leads module (that lives under /relaties). */}
                <Route element={<RequireRole allow={['admin', 'team']} />}>
                <Route element={<RequireFundraisingEnabled />}>
                  <Route path="/fundraising" element={<FundraisingLayout />}>
                    <Route index element={<FundraisingDashboardPage />} />
                    <Route path="funnel" element={<FundraisingBoardPage />} />
                    <Route path="investeerders" element={<InvestorsPage />} />
                    <Route path="investeerders/:id" element={<InvestorDetailPage />} />
                    <Route path="suggesties" element={<SuggestiesPage />} />
                    <Route path="mails" element={<MailsPage />} />
                    {/* Unknown /fundraising sub-paths dead-end here too (spec-30):
                        the guard already redirects everything while disabled; when
                        enabled, unmatched sub-paths fall through to the dashboard. */}
                    <Route path="*" element={<Navigate to="/fundraising" replace />} />
                  </Route>
                </Route>
                </Route>
                {/* Relaties (spec-44): CRM-module voor contacten, organisaties en
                    lijsten. Zelfde gating als pre-invest: admin/team via
                    RequireRole; de layout zelf toont bovendien een
                    geen-toegang-melding als UX-vangnet. */}
                <Route path="/relaties" element={<RelatiesLayout />}>
                  {/* Role-based index: the fundraiser's home is the fund-leads
                      connections board; admin/team land on the CRM overview. */}
                  <Route index element={<RelatiesIndexRedirect />} />
                  {/* Private fund-leads boards (spec-54): admin + fundraiser
                      only, enforced by the backend; the layout hides the rest of
                      the nav for the fundraiser. */}
                  <Route
                    path="fundraising"
                    element={<Navigate to="/relaties/fundraising/connecties" replace />}
                  />
                  <Route path="fundraising/connecties" element={<FundLeadsConnectionsPage />} />
                  <Route path="fundraising/leads" element={<FundLeadsLeadsPage />} />
                  <Route path="fundraising/leads/:id" element={<FundLeadsLeadDetailPage />} />
                  <Route path="overzicht" element={<OverzichtPage />} />
                  <Route path="contacten" element={<ContactsPage />} />
                  <Route path="contacten/:personId" element={<ContactDetailPage />} />
                  {/* spec-63: organisations with their contact persons. */}
                  <Route path="organisaties" element={<OrganisationsPage />} />
                  <Route path="organisaties/:organisationId" element={<OrganisationPage />} />
                  <Route path="afspraken" element={<AgreementsPage />} />
                  {/* spec-63 E7: Inspreken (mobiel). */}
                  <Route path="inspreken" element={<InsprekenPage />} />
                  <Route path="inspreken/:id" element={<InsprekenPage />} />
                  <Route element={<RequireRole allow={['admin', 'team']} />}>
                    <Route path="taken" element={<MyTasksPage module="relaties" />} />
                  </Route>
                  {/* spec-63 E5: events are lists of the kind Event; the old addresses redirect. */}
                  <Route path="events" element={<Navigate to="/relaties/lijsten?soort=event" replace />} />
                  <Route path="events/:eventId" element={<EventRedirect />} />
                  <Route path="lijsten" element={<ListsPage />} />
                  <Route path="lijsten/:listId" element={<ListPage />} />
                  {/* spec-63: categories and labels (Beheer). */}
                  <Route path="instellingen" element={<CrmSettingsPage />} />
                  {/* spec-63 E6: Excel-import van contacten en organisaties (Beheer). */}
                  <Route path="importeren" element={<CrmImportPage />} />
                  <Route path="importeren/:id" element={<CrmImportResultPage />} />
                  {/* Removed CRM sections (spec-59): old deeplinks land on the Relaties
                      start page. */}
                  {removedCrmRoutes}
                </Route>
                <Route path="/postinvest" element={<PostinvestLayout />}>
                  <Route index element={<PortfolioListPage />} />
                  <Route path="startup/:itemId" element={<PostinvestDossierPage />} />
                  <Route path="portal/:portalStartupId" element={<PortalRedirectPage />} />
                  <Route path="doorverwijzingen" element={<ReferralsPage />} />
                  <Route path="documenten" element={<PostinvestDocumentsPage />} />
                  <Route element={<RequireRole allow={['admin', 'team']} />}>
                    <Route path="taken" element={<MyTasksPage module="postinvest" />} />
                  </Route>
                  {/* spec-56 legacy redirects — old wasstraat URLs keep working as
                      long as links in mails/bookmarks exist. */}
                  <Route path="wasstraat" element={<Navigate to="/postinvest" replace />} />
                  <Route path="wasstraat/:itemId" element={<WasstraatLegacyRedirect />} />
                  <Route path="*" element={<NotFoundPage />} />
                </Route>
              </Route>
            </Route>
            </Route>
          </Route>
          {/* Founder portal: founder-only; admin/team bounce back to /.
              spec-36 §5.1: the founder environment is a single scrollable page
              (`FounderHomePage`) instead of tabbed sub-routes. Deep-links from
              existing invite mails (`/founder/forms`, `/founder/financial-dd`)
              redirect to the shell so the mail-links keep working. The
              individual form detail keeps its own route (no shell). */}
          <Route element={<RequireRole allow={['founder']} />}>
            <Route path="/founder" element={<FounderLayout />}>
              <Route index element={<FounderHomePage />} />
              <Route path="forms" element={<Navigate to="/founder" replace />} />
              <Route path="forms/:id" element={<FounderFormDetailPage />} />
              <Route
                path="financial-dd"
                element={<Navigate to="/founder" replace />}
              />
            </Route>
          </Route>
          {/* Anything else behind the login: a standalone 404 (the CRM routes fall back here). */}
          <Route path="*" element={<NotFoundPage standalone />} />
        </Route>
      </Route>
    </Routes>
    </Suspense>
  )
}

export default App
